import type {
  SimpleChartData,
  SimpleChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import {
  CurrencyValue,
  LineChart,
  BarChart,
  formatNumber
} from '@blockanalitica/ui';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useChartFillDatasetsDates } from '@/hooks';
import { useParams } from 'react-router';

export default function HistoricGraphs({
  data,
  type,
  chartDaysAgo,
  currencySymbol
}: {
  data: SimpleChartData[];
  type: string;
  chartDaysAgo: string;
  currencySymbol: string;
}) {
  const theme = useTheme();
  const { marketAddress, network } = useParams() as {
    marketAddress: string;
    network: string;
  };

  const yScaleValueFormatter = useCallback((value: string | number) => {
    return formatNumber(Number(value));
  }, []);

  const datasets = useMemo(() => {
    let datasets: SimpleChartDataset[];

    if (type === 'supply_withdraw') {
      datasets = [
        {
          xField: 'date',
          yField: 'supply_volume',
          label: 'Supply',
          backgroundColor: theme.colors.chartUp
        },
        {
          xField: 'date',
          yField: 'withdraw_volume',
          label: 'Withdraw',
          backgroundColor: theme.colors.chartDown
        }
      ];
    } else if (type === 'net_flow') {
      datasets = [
        {
          xField: 'date',
          yField: 'net_flow',
          label: 'Net',
          backgroundColor: theme.colors.primary
        }
      ];
    } else if (type === 'borrow_repay') {
      datasets = [
        {
          xField: 'date',
          yField: 'borrow_volume',
          label: 'Borrow',
          backgroundColor: theme.colors.chartUp
        },
        {
          xField: 'date',
          yField: 'repay_volume',
          label: 'Repay',
          backgroundColor: theme.colors.chartDown
        }
      ];
    } else {
      datasets = [
        {
          xField: 'date',
          yField: 'active_wallets'
        }
      ];
    }

    return datasets;
  }, [type, theme.colors]);

  const valueFormatter = useCallback(
    (value: string | number) => (
      <CurrencyValue
        value={value}
        options={{
          currencySize: type === 'net_flow' ? '3' : '0',
          currency: {
            name: currencySymbol,
            address: marketAddress,
            network: network
          }
        }}
      />
    ),
    [currencySymbol, marketAddress, network, type]
  );

  const chartData = useChartFillDatasetsDates(data, datasets, chartDaysAgo);

  if (type === 'active_wallets') {
    return (
      <LineChart
        key={type}
        data={chartData}
        datasets={datasets}
        yScaleValueFormatter={yScaleValueFormatter}
        daysAgo={chartDaysAgo}
      />
    );
  } else {
    return (
      <BarChart
        key={type}
        data={data}
        datasets={datasets}
        valueFormatter={valueFormatter}
        yScaleValueFormatter={yScaleValueFormatter}
        daysAgo={chartDaysAgo}
      />
    );
  }
}
